import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import {
  LogOut, Download, RefreshCw, Users, Mail, Heart, CalendarClock,
  Search, ShieldCheck,
} from "lucide-react";

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "elders", label: "Elders (Maa Papa Care)", icon: Heart },
  { id: "appointments", label: "Appointments", icon: CalendarClock },
];

function StatChip({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 min-w-[130px]">
      <div className="text-2xl font-semibold text-[#041E26]">{value ?? "—"}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function DataTable({ rows, columns, emptyLabel }) {
  if (!rows || rows.length === 0) {
    return <div className="p-10 text-center text-slate-400 bg-slate-50 rounded-xl">{emptyLabel}</div>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className="text-left px-3 py-2.5 font-semibold whitespace-nowrap">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={r.id || i} className="hover:bg-slate-50/60">
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2.5 text-slate-700 max-w-[260px] truncate">
                  {c.render ? c.render(r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const USER_COLS = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role", render: (r) => <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#B45309] text-[11px] font-semibold uppercase">{r.role}</span> },
  { key: "identifier", label: "Login ID (Phone/Email)", render: (r) => <span className="font-mono text-[12px] text-[#041E26]">{r.identifier || "—"}</span> },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "specialty", label: "Specialty" },
  { key: "address_city", label: "City" },
  { key: "address_state", label: "State" },
  { key: "address_pincode", label: "PIN" },
  { key: "fee", label: "Fee (₹)" },
  { key: "verified", label: "Verified", render: (r) => (r.verified ? "✓" : "—") },
  { key: "created_at", label: "Joined", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—") },
];

const ENQ_COLS = [
  { key: "contact_name", label: "Name" },
  { key: "company", label: "Company/Family" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "employees", label: "Size" },
  { key: "message", label: "Message" },
  { key: "created_at", label: "Received", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—") },
];

const ELDER_COLS = [
  { key: "name", label: "Elder Name" },
  { key: "age", label: "Age" },
  { key: "gender", label: "Gender" },
  { key: "city", label: "City" },
  { key: "conditions", label: "Conditions" },
  { key: "emergency_contact", label: "Emergency Contact" },
  { key: "active_plan", label: "Plan" },
  { key: "monthly_price", label: "₹/mo" },
  { key: "created_at", label: "Enrolled", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "—") },
];

const APPT_COLS = [
  { key: "patient_name", label: "Patient" },
  { key: "doctor_name", label: "Doctor" },
  { key: "doctor_specialty", label: "Specialty" },
  { key: "slot_time", label: "Slot", render: (r) => (r.slot_time ? new Date(r.slot_time).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—") },
  { key: "fee", label: "Fee (₹)" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Booked", render: (r) => (r.created_at ? new Date(r.created_at).toLocaleDateString("en-IN") : "—") },
];

const COL_MAP = { users: USER_COLS, enquiries: ENQ_COLS, elders: ELDER_COLS, appointments: APPT_COLS };

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("users");
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user) { setChecking(false); return; }
    api.get("/admin/whoami").then(({ data }) => {
      setIsAdmin(!!data.is_admin);
      setChecking(false);
      if (data.is_admin) {
        api.get("/admin/stats").then(({ data: d }) => setStats(d.counts)).catch(() => {});
      }
    }).catch(() => setChecking(false));
  }, [user]);

  const load = async (activeTab) => {
    setLoading(true);
    try {
      const endpoint = { users: "/admin/users", enquiries: "/admin/enquiries", elders: "/admin/elders", appointments: "/admin/appointments" }[activeTab];
      const listKey = { users: "users", enquiries: "enquiries", elders: "elders", appointments: "appointments" }[activeTab];
      const { data } = await api.get(endpoint);
      setRows(data[listKey] || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(tab); }, [tab, isAdmin]);

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export.xlsx", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `candle-healthcare-export-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel exported — check your Downloads folder");
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const filtered = filter.trim()
    ? rows.filter((r) => JSON.stringify(r).toLowerCase().includes(filter.trim().toLowerCase()))
    : rows;

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Checking admin access…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
        <Card className="p-8 max-w-md text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-[#B45309] mb-2" />
          <h2 className="font-head text-xl font-semibold text-[#041E26]">Admin sign-in required</h2>
          <p className="text-sm text-slate-500 mt-2 mb-5">Please sign in with the admin email to access this panel.</p>
          <Button data-testid="admin-goto-home" onClick={() => navigate("/")} className="rounded-full bg-[#041E26] text-white">Go to home & sign in</Button>
        </Card>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] p-6">
        <Card className="p-8 max-w-md text-center">
          <h2 className="font-head text-xl font-semibold text-[#041E26]">Access denied</h2>
          <p className="text-sm text-slate-500 mt-2 mb-5">This panel is available only to the Candle Healthcare admin. If this is a mistake, sign in with the admin email.</p>
          <div className="flex gap-2 justify-center">
            <Button data-testid="admin-back-dash" onClick={() => navigate("/dashboard")} variant="outline" className="rounded-full">My dashboard</Button>
            <Button data-testid="admin-logout" onClick={() => { logout(); navigate("/"); }} className="rounded-full bg-[#041E26] text-white">Sign out</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-[#041E26] px-6 lg:px-10 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button data-testid="admin-logo" onClick={() => navigate("/")} className="flex items-center gap-2">
            <Logo className="h-10" onDark showTagline />
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs uppercase tracking-widest text-[#F59E0B] font-semibold">Admin Panel</span>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="outline"
              className="rounded-full gap-2 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white">
              <LogOut className="w-4 h-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-head text-3xl md:text-4xl font-light text-[#041E26]">
              Data <span className="text-[#B45309] font-normal">Cockpit</span>
            </h1>
            <p className="text-slate-500 mt-1">Every user, enquiry, elder & appointment — with one-click Excel export.</p>
          </div>
          <Button data-testid="admin-export-xlsx" onClick={downloadExcel} disabled={exporting}
            className="rounded-full h-11 px-5 bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold gap-2 shadow-md">
            <Download className="w-4 h-4" />
            {exporting ? "Preparing…" : "Download full Excel"}
          </Button>
        </motion.div>

        {/* Stats row */}
        {stats && (
          <div className="flex flex-wrap gap-3 mt-7">
            <StatChip label="Total users" value={stats.total_users} />
            <StatChip label="Patients" value={stats.patient} />
            <StatChip label="Doctors" value={stats.doctor} />
            <StatChip label="Therapists" value={stats.therapist} />
            <StatChip label="Families" value={stats.family} />
            <StatChip label="Sathis" value={stats.sathi} />
            <StatChip label="Enterprise" value={stats.enterprise} />
            <StatChip label="Enquiries" value={stats.enquiries} />
            <StatChip label="Elders" value={stats.elders} />
            <StatChip label="Appointments" value={stats.appointments} />
            <StatChip label="Active plans" value={stats.active_subscriptions} />
            <StatChip label="Medical records" value={stats.medical_records} />
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-[#B45309] text-[#041E26]"
                  : "border-transparent text-slate-500 hover:text-[#041E26]"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
              <span className="ml-1 text-[11px] text-slate-400">{tab === t.id && !loading ? `(${filtered.length})` : ""}</span>
            </button>
          ))}
        </div>

        {/* Search + refresh */}
        <div className="flex items-center gap-3 mt-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="admin-search" value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name, email, phone, city…" className="pl-9 h-10 rounded-lg" />
          </div>
          <Button data-testid="admin-refresh" onClick={() => load(tab)} variant="outline"
            className="h-10 rounded-lg gap-2"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>

        {/* Table */}
        <div className="mt-4">
          <DataTable
            rows={filtered}
            columns={COL_MAP[tab]}
            emptyLabel={loading ? "Loading…" : `No ${tab} yet.`}
          />
        </div>
      </main>
    </div>
  );
}
